import * as React from "react"

import { cn } from "@/lib/utils"

import type { Message } from "../../data/review-data"
import { FindingThread } from "../checklist/finding-thread"
import { DivergencePair } from "../divergence-pair"
import { FindingContext, FindingNumber, MessageButton, SeverityPill } from "../finding-parts"
import { isCorrected, type ReportFindingItem } from "../report-data"

import { findingStatus } from "./list-data"

export interface FindingRowProps {
  finding: ReportFindingItem
  thread: readonly Message[]
  unread: number
  /**
   * Omitted, with `onMarkRead`, for a reader who may read the conversation but
   * not take part (a viewer): the thread opens read-only and is not marked read,
   * since both are writes the server refuses them.
   */
  onSend?: ((body: string) => void) | undefined
  onMarkRead?: (() => void) | undefined
}

/**
 * One finding in the list: the same header as the checklist card, then the
 * printed wording beside the approved one. Read-only apart from the thread,
 * which opens in place under the row.
 */
export function FindingRow({
  finding,
  thread,
  unread,
  onSend,
  onMarkRead,
}: FindingRowProps): JSX.Element {
  const [open, setOpen] = React.useState(false)
  const headingId = React.useId()
  const settled = isCorrected(finding) || finding.clientResolution === "done"
  const repeated = finding.occurrenceCount > 1

  const toggle = (): void => {
    // Marking read is a write; a read-only reader opens the thread without it.
    if (!open) onMarkRead?.()
    setOpen(!open)
  }

  return (
    <article
      aria-labelledby={headingId}
      className="rounded-lg border border-border bg-surface p-5 shadow-e1"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
        <FindingNumber number={finding.number} settled={settled} />
        <SeverityPill severity={finding.severity} />
        <span
          title={`Appears ${String(finding.occurrenceCount)} time${repeated ? "s" : ""} on the label`}
          className={cn(
            "inline-flex shrink-0 items-center rounded-full px-2 py-1 text-[10.5px] font-semibold leading-none tabular-nums",
            repeated ? "bg-brand-tint text-brand" : "bg-surface-sunken text-text-3",
          )}
        >
          {finding.occurrenceCount}× on label
        </span>
        <div className="ml-auto">
          <MessageButton
            onClick={toggle}
            canPost={onSend !== undefined}
            unread={unread}
            open={open}
            expanded={open}
          />
        </div>
      </div>

      <h3 id={headingId} className="mt-3 text-heading text-text">
        {finding.title}
      </h3>
      <FindingContext finding={finding} trailing={findingStatus(finding)} />

      {finding.ruleCheck && (
        <p className="mt-3 text-body-sm text-text-2">What we check: {finding.ruleCheck}</p>
      )}

      <DivergencePair
        className="mt-3"
        printedText={finding.printedText}
        masterText={finding.masterText}
        fallback={finding.divergence}
      />

      {finding.citations.length > 0 && (
        <p className="mt-3 text-[11px] leading-relaxed text-text-3">
          Authority: {finding.citations.join("; ")}
        </p>
      )}

      {open && (
        <div
          role="region"
          aria-label={`Messages about finding ${String(finding.number)}`}
          className="mt-4 flex h-[320px] min-h-0 flex-col border-t border-border-soft pt-1"
        >
          <FindingThread messages={thread} onSend={onSend} focusComposer />
        </div>
      )}
    </article>
  )
}
